import { useEffect, useMemo, useState } from 'react';
import {
  DIAGNOSTIC_ANSWER_OPTIONS,
  DIAGNOSTIC_SECTIONS,
  IGPE_LEVEL_BANDS,
  computeDiagnosticIgpe,
  diagnosticFormComplete,
  emptyDiagnosticAnswers,
  emptyDiagnosticObservations,
  formatConclusionReportText,
  igpeLevelLabel,
  type DiagnosticAnswers,
  type DiagnosticAnswerValue,
  type DiagnosticIgpeResult,
  type DiagnosticObservations,
} from '@isbd/shared';
import { kuunda } from '@/lib/kuunda';
import { useAuth } from '@/features/auth/AuthProvider';
import { downloadTextFile } from '@/lib/downloadText';
import { buildDiagnosticPdfHtml, openPrintablePdf } from '@/lib/exportPdf';
import { ListSkeleton } from '@/features/ui/skeleton';
import { useToast } from '@/features/ui/toast/useToast';
import './diagnostic.css';

interface ReportMeta {
  title: string;
  domain: string;
  zone: string;
  problems: string;
  solution: string;
  porter: string;
}

interface Props {
  ideationId: string;
  businessIdea: string;
  meta?: Partial<ReportMeta>;
  onDone?: (result: DiagnosticIgpeResult) => void;
}

export function DiagnosticWizard({ ideationId, businessIdea, meta, onDone }: Props) {
  const { profile } = useAuth();
  const toast = useToast();
  const [sectionIdx, setSectionIdx] = useState(0);
  const [answers, setAnswers] = useState<DiagnosticAnswers>(emptyDiagnosticAnswers);
  const [observations, setObservations] = useState<DiagnosticObservations>(emptyDiagnosticObservations);
  const [reportMeta, setReportMeta] = useState<ReportMeta>({
    title: meta?.title || businessIdea,
    domain: meta?.domain || '',
    zone: meta?.zone || '',
    problems: meta?.problems || '',
    solution: meta?.solution || businessIdea,
    porter:
      meta?.porter ||
      (profile ? `${profile.first_name ?? ''} ${profile.last_name ?? ''}`.trim() : ''),
  });
  const [result, setResult] = useState<DiagnosticIgpeResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingExisting, setLoadingExisting] = useState(true);

  useEffect(() => {
    setReportMeta((prev) => ({
      ...prev,
      title: meta?.title || businessIdea || prev.title,
      solution: meta?.solution || businessIdea || prev.solution,
      domain: meta?.domain ?? prev.domain,
      zone: meta?.zone ?? prev.zone,
      problems: meta?.problems ?? prev.problems,
      porter:
        meta?.porter ||
        (profile ? `${profile.first_name ?? ''} ${profile.last_name ?? ''}`.trim() : prev.porter),
    }));
  }, [businessIdea, meta, profile]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoadingExisting(true);
      const { data, error: loadErr } = await kuunda
        .from('diagnostic_reports')
        .select('*')
        .eq('ideation_id', ideationId)
        .maybeSingle();
      if (cancelled) return;
      if (loadErr) {
        toast.error(loadErr.message);
        setLoadingExisting(false);
        return;
      }
      if (data) {
        const ctx = (data.context_answers || {}) as {
          answers?: DiagnosticAnswers;
          observations?: DiagnosticObservations;
          report_meta?: Partial<ReportMeta>;
        };
        const restoredAnswers = ctx.answers
          ? { ...emptyDiagnosticAnswers(), ...ctx.answers }
          : null;
        const restoredObs = ctx.observations
          ? { ...emptyDiagnosticObservations(), ...ctx.observations }
          : emptyDiagnosticObservations();
        if (restoredAnswers) setAnswers(restoredAnswers);
        setObservations(restoredObs);
        if (ctx.report_meta) {
          setReportMeta((prev) => ({ ...prev, ...ctx.report_meta }));
        }
        if (restoredAnswers && diagnosticFormComplete(restoredAnswers)) {
          const computed = computeDiagnosticIgpe(restoredAnswers, restoredObs);
          setResult(computed);
          onDone?.(computed);
        } else {
          const ai = (data.ai_raw || {}) as {
            total_obtenu?: number;
            total_cible?: number;
            recommendation?: string;
          };
          const restored: DiagnosticIgpeResult = {
            igpe_score: Number(data.igpe_score) || 0,
            igpe_level: data.igpe_level,
            total_obtenu: Number(ai.total_obtenu) || 0,
            total_cible: Number(ai.total_cible) || 2452,
            dimensions: (data.dimensions || []) as DiagnosticIgpeResult['dimensions'],
            vulnerabilities: (data.vulnerabilities || []) as DiagnosticIgpeResult['vulnerabilities'],
            summary: data.summary || '',
            recommendation: ai.recommendation || data.summary || '',
            report_results:
              data.report_results ||
              data.summary ||
              `Score IGPE ${Number(data.igpe_score) || 0} %.`,
            report_strengths: data.report_strengths
              ? String(data.report_strengths).split('\n').filter(Boolean)
              : [],
            report_weaknesses: data.report_weaknesses
              ? String(data.report_weaknesses).split('\n').filter(Boolean)
              : [],
            report_recommendations: data.report_recommendations
              ? String(data.report_recommendations).split('\n').filter(Boolean)
              : [ai.recommendation || data.summary || ''].filter(Boolean),
          };
          setResult(restored);
          if (data.igpe_score != null) onDone?.(restored);
        }
        toast.info(`Diagnostic déjà enregistré — IGPE ${Number(data.igpe_score).toFixed(0)} %.`);
      }
      setLoadingExisting(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [ideationId]);

  const section = DIAGNOSTIC_SECTIONS[sectionIdx]!;
  const answeredCount = useMemo(
    () => Object.values(answers).filter((v) => v === 'oui' || v === 'non' || v === 'partiel').length,
    [answers],
  );
  const totalQuestions = useMemo(
    () => DIAGNOSTIC_SECTIONS.reduce((n, s) => n + s.questions.length, 0),
    [],
  );
  const progressPct = totalQuestions ? Math.round((answeredCount / totalQuestions) * 100) : 0;
  const formComplete = diagnosticFormComplete(answers);

  async function requestDiagnostic() {
    if (!profile) return;
    if (!formComplete) {
      toast.error('Répondez à tous les critères (Oui / Non / Partiel) avant de demander le diagnostic.');
      return;
    }
    setLoading(true);
    try {
      const computed = computeDiagnosticIgpe(answers, observations);
      setResult(computed);

      const { error: upErr } = await kuunda.from('diagnostic_reports').upsert(
        {
          user_id: profile.id,
          ideation_id: ideationId,
          context_answers: { answers, observations, report_meta: reportMeta },
          igpe_score: computed.igpe_score,
          igpe_level: computed.igpe_level,
          dimensions: computed.dimensions,
          vulnerabilities: computed.vulnerabilities,
          summary: computed.summary,
          report_results: computed.report_results,
          report_strengths: computed.report_strengths.join('\n'),
          report_weaknesses: computed.report_weaknesses.join('\n'),
          report_recommendations: computed.report_recommendations.join('\n'),
          ai_raw: {
            total_obtenu: computed.total_obtenu,
            total_cible: computed.total_cible,
            recommendation: computed.recommendation,
          },
        },
        { onConflict: 'ideation_id' },
      );
      if (upErr) throw upErr;
      toast.success(`Diagnostic généré — IGPE ${computed.igpe_score} % (${igpeLevelLabel(computed.igpe_level)}).`);
      onDone?.(computed);
      requestAnimationFrame(() => {
        document.getElementById('diagnostic-report')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Diagnostic impossible');
    } finally {
      setLoading(false);
    }
  }

  function downloadReport() {
    if (!result) return;
    const conclusion = formatConclusionReportText(
      'Rapport de diagnostic d’activité entrepreneuriale',
      {
        results: result.report_results,
        strengths: result.report_strengths,
        weaknesses: result.report_weaknesses,
        recommendations: result.report_recommendations,
        viability: result.igpe_score >= 50 ? 'viable' : 'not_viable',
      },
      {
        'Intitulé projet': reportMeta.title || '—',
        'Domaine d’activité': reportMeta.domain || '—',
        'Zone d’opération': reportMeta.zone || '—',
        'Besoins/problèmes adressés': reportMeta.problems || '—',
        'Solution entrepreneuriale': reportMeta.solution || '—',
        'Porteur du projet': reportMeta.porter || '—',
        'Score IGPE': `${result.igpe_score} % (${igpeLevelLabel(result.igpe_level)})`,
      },
    );
    const detail = [
      '',
      'Annexe — Tableau des dimensions',
      'Dimensions | Note cible | Coefficient | Total cible | Résultats',
      ...result.dimensions.map(
        (d) =>
          `${d.label} | ${d.note_cible} | ${d.coefficient} | ${d.total_cible} | ${d.resultat}`,
      ),
      `Total | | | ${result.total_cible} | ${result.total_obtenu}`,
      '',
      'Orientation pour l’interprétation :',
      ...IGPE_LEVEL_BANDS.map((b) => `${b.min}–${b.max} % : ${b.label} — ${b.recommendation}`),
    ].join('\n');
    downloadTextFile(`rapport-diagnostic-igpe-${ideationId.slice(0, 8)}.txt`, conclusion + detail);
  }

  function downloadReportPdf() {
    if (!result) return;
    openPrintablePdf(
      'Rapport de diagnostic d’activité entrepreneuriale',
      buildDiagnosticPdfHtml({
        meta: {
          'Intitulé projet': reportMeta.title || '—',
          'Domaine d’activité': reportMeta.domain || '—',
          'Zone d’opération': reportMeta.zone || '—',
          'Besoins / problèmes adressés': reportMeta.problems || '—',
          'Solution entrepreneuriale': reportMeta.solution || '—',
          'Porteur du projet': reportMeta.porter || '—',
        },
        igpeScore: result.igpe_score,
        igpeLevel: igpeLevelLabel(result.igpe_level),
        totalObtenu: result.total_obtenu,
        totalCible: result.total_cible,
        dimensions: result.dimensions,
        results: result.report_results,
        strengths: result.report_strengths,
        weaknesses: result.report_weaknesses,
        recommendations: result.report_recommendations,
      }),
    );
  }

  function setAnswer(id: string, value: DiagnosticAnswerValue) {
    setAnswers((prev) => ({ ...prev, [id]: value }));
  }

  return (
    <div className="igpe-page">
      <div className="igpe-hero">
        <div>
          <h3>Formulaire de diagnostic d’activité</h3>
          <p>
            Évaluation de l’Indice Global de Performance Entrepreneuriale (IGPE). Renseignez chaque
            critère avec Oui / Non / Partiel, puis générez votre rapport.
          </p>
        </div>
        <div className="igpe-score-pill">
          <div>
            <div className="eyebrow">Progression</div>
            <strong>
              {answeredCount}/{totalQuestions}
            </strong>
          </div>
          <div style={{ flex: 1, minWidth: '5rem' }}>
            <div className="igpe-progress-track" aria-hidden>
              <div className="igpe-progress-fill" style={{ width: `${progressPct}%` }} />
            </div>
            <div className="muted" style={{ fontSize: '0.8rem', marginTop: '0.25rem' }}>
              {progressPct} %
            </div>
          </div>
        </div>
      </div>

      {loadingExisting ? <ListSkeleton rows={3} /> : null}

      <section className="igpe-meta-form">
        <div className="field">
          <label htmlFor="igpe-title">Intitulé projet</label>
          <input
            id="igpe-title"
            value={reportMeta.title}
            onChange={(e) => setReportMeta({ ...reportMeta, title: e.target.value })}
          />
        </div>
        <div className="field">
          <label htmlFor="igpe-domain">Domaine d’activité</label>
          <input
            id="igpe-domain"
            value={reportMeta.domain}
            onChange={(e) => setReportMeta({ ...reportMeta, domain: e.target.value })}
            placeholder="Ex. agroalimentaire, services…"
          />
        </div>
        <div className="field">
          <label htmlFor="igpe-zone">Zone d’opération</label>
          <input
            id="igpe-zone"
            value={reportMeta.zone}
            onChange={(e) => setReportMeta({ ...reportMeta, zone: e.target.value })}
            placeholder="Ex. Abidjan, national…"
          />
        </div>
        <div className="field">
          <label htmlFor="igpe-porter">Porteur du projet</label>
          <input
            id="igpe-porter"
            value={reportMeta.porter}
            onChange={(e) => setReportMeta({ ...reportMeta, porter: e.target.value })}
          />
        </div>
        <div className="field" style={{ gridColumn: '1 / -1' }}>
          <label htmlFor="igpe-problems">Besoins / problèmes adressés</label>
          <textarea
            id="igpe-problems"
            rows={2}
            value={reportMeta.problems}
            onChange={(e) => setReportMeta({ ...reportMeta, problems: e.target.value })}
          />
        </div>
        <div className="field" style={{ gridColumn: '1 / -1' }}>
          <label htmlFor="igpe-solution">Solution entrepreneuriale</label>
          <textarea
            id="igpe-solution"
            rows={2}
            value={reportMeta.solution}
            onChange={(e) => setReportMeta({ ...reportMeta, solution: e.target.value })}
          />
        </div>
      </section>

      <div className="igpe-letters" role="tablist" aria-label="Sections du diagnostic">
        {DIAGNOSTIC_SECTIONS.map((s, idx) => {
          const done = s.questions.every(
            (q) => answers[q.id] === 'oui' || answers[q.id] === 'non' || answers[q.id] === 'partiel',
          );
          return (
            <button
              key={s.key}
              type="button"
              role="tab"
              aria-selected={sectionIdx === idx}
              title={`${s.letter}. ${s.label}`}
              className={`igpe-letter ${sectionIdx === idx ? 'is-active' : ''} ${done ? 'is-done' : ''}`}
              onClick={() => setSectionIdx(idx)}
            >
              {s.letter}
              {done ? <span className="igpe-letter-check">✓</span> : null}
            </button>
          );
        })}
      </div>

      <section className="igpe-section-card">
        <div className="igpe-section-head">
          <div>
            <h4>
              {section.letter}. {section.label}
            </h4>
          </div>
          <div className="igpe-section-meta">
            <span className="igpe-meta-chip">Note cible {section.noteCible} pts</span>
            <span className="igpe-meta-chip">Coef. {section.coefficient}</span>
            <span className="igpe-meta-chip">Total cible {section.totalCible}</span>
          </div>
        </div>

        <table className="igpe-table">
          <thead>
            <tr>
              <th style={{ width: '42%' }}>Critères</th>
              <th style={{ width: '28%' }}>Réponses (Oui, Non, Partiel)</th>
              <th>Observations</th>
            </tr>
          </thead>
          <tbody>
            {section.questions.map((q) => (
              <tr key={q.id}>
                <td data-label="Critère">
                  <p className="igpe-criterion">{q.label}</p>
                </td>
                <td data-label="Réponse">
                  <div className="igpe-answers" role="group" aria-label={q.label}>
                    {DIAGNOSTIC_ANSWER_OPTIONS.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        className={`igpe-answer is-${opt.value} ${answers[q.id] === opt.value ? 'is-on' : ''}`}
                        onClick={() => setAnswer(q.id, opt.value)}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </td>
                <td data-label="Observations">
                  <input
                    className="igpe-obs"
                    value={observations[q.id] ?? ''}
                    placeholder="Optionnel"
                    onChange={(e) =>
                      setObservations((prev) => ({ ...prev, [q.id]: e.target.value }))
                    }
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <p className="igpe-scoring-hint">
          Calcul automatique — Oui = 4 · Non = 1 · Partiel = 2. Le résultat de la dimension =
          note obtenue × coefficient.
        </p>
      </section>

      <div className="igpe-actions">
        <button
          type="button"
          className="btn btn-ghost"
          disabled={sectionIdx === 0}
          onClick={() => setSectionIdx((i) => Math.max(0, i - 1))}
        >
          Section précédente
        </button>
        <button
          type="button"
          className="btn btn-ghost"
          disabled={sectionIdx >= DIAGNOSTIC_SECTIONS.length - 1}
          onClick={() => setSectionIdx((i) => Math.min(DIAGNOSTIC_SECTIONS.length - 1, i + 1))}
        >
          Section suivante
        </button>
        <button
          type="button"
          className="btn btn-primary"
          disabled={loading || !formComplete}
          onClick={() => void requestDiagnostic()}
        >
          {loading ? 'Calcul…' : 'Demander mon diagnostic'}
        </button>
        {!formComplete ? (
          <span className="muted" style={{ fontSize: '0.9rem' }}>
            Complétez les {totalQuestions - answeredCount} critères restants.
          </span>
        ) : null}
      </div>

      {result ? (
        <article id="diagnostic-report" className="igpe-report">
          <div className="igpe-report-banner">
            <div>
              <h3>Rapport de diagnostic d’activité entrepreneuriale</h3>
              <p>
                Analyse générée automatiquement à partir de votre formulaire. Vous pouvez
                télécharger une copie de ce rapport.
              </p>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <button type="button" className="btn btn-primary" onClick={downloadReportPdf}>
                Télécharger en PDF
              </button>
              <button type="button" className="btn" onClick={downloadReport}>
                Copie texte
              </button>
            </div>
          </div>

          <div className="igpe-report-body">
            <div className="igpe-report-grid">
              <div className="igpe-card">
                <h4>Informations projet</h4>
                <table className="igpe-kv">
                  <tbody>
                    <tr>
                      <th>Intitulé projet</th>
                      <td>{reportMeta.title || '—'}</td>
                    </tr>
                    <tr>
                      <th>Domaine d’activité</th>
                      <td>{reportMeta.domain || '—'}</td>
                    </tr>
                    <tr>
                      <th>Zone d’opération</th>
                      <td>{reportMeta.zone || '—'}</td>
                    </tr>
                    <tr>
                      <th>Besoins / problèmes</th>
                      <td>{reportMeta.problems || '—'}</td>
                    </tr>
                    <tr>
                      <th>Solution entrepreneuriale</th>
                      <td>{reportMeta.solution || '—'}</td>
                    </tr>
                    <tr>
                      <th>Porteur du projet</th>
                      <td>{reportMeta.porter || '—'}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="igpe-card">
                <h4>Résultats IGPE</h4>
                <div className="igpe-result-hero">
                  <div className="eyebrow">Indice global</div>
                  <div className="igpe-big">{result.igpe_score}%</div>
                  <div className={`igpe-level ${result.igpe_level}`}>
                    {igpeLevelLabel(result.igpe_level)}
                  </div>
                  <p className="muted" style={{ margin: '0.35rem 0 0', fontSize: '0.85rem' }}>
                    ({result.total_obtenu} / {result.total_cible}) × 100
                  </p>
                </div>
              </div>
            </div>

            <div className="igpe-card">
              <h4>Tableau des dimensions</h4>
              <div className="table-wrap">
                <table className="igpe-dims">
                  <thead>
                    <tr>
                      <th>Dimensions</th>
                      <th>Note cible</th>
                      <th>Coefficient</th>
                      <th>Total cible</th>
                      <th>Résultats</th>
                      <th>% cible</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.dimensions.map((d) => (
                      <tr key={d.key} className={d.is_vulnerable ? 'is-vuln' : undefined}>
                        <td>
                          {d.label}
                          {d.is_vulnerable ? ' ✶' : ''}
                        </td>
                        <td>{d.note_cible} pts</td>
                        <td>{d.coefficient}</td>
                        <td>{d.total_cible}</td>
                        <td>
                          <strong>{d.resultat}</strong>
                        </td>
                        <td>{Math.round(d.pct_cible)} %</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td>Total</td>
                      <td />
                      <td />
                      <td>{result.total_cible}</td>
                      <td>{result.total_obtenu}</td>
                      <td>{result.igpe_score} %</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            <div className="igpe-card">
              <h4>Conclusions (format standard)</h4>
              <div style={{ padding: '0.35rem 1rem 0.85rem' }}>
                <p>
                  <strong>1. Résultats</strong>
                  <br />
                  {result.report_results}
                </p>
                <p>
                  <strong>2. Points forts</strong>
                </p>
                <ul>
                  {result.report_strengths.map((s) => (
                    <li key={s}>{s}</li>
                  ))}
                </ul>
                <p>
                  <strong>3. Points faibles</strong>
                </p>
                <ul>
                  {result.report_weaknesses.map((s) => (
                    <li key={s}>{s}</li>
                  ))}
                </ul>
                <p>
                  <strong>4. Recommandations</strong>
                </p>
                <ul>
                  {result.report_recommendations.map((s) => (
                    <li key={s}>{s}</li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="igpe-reco">
              <strong>Recommandation</strong>
              {result.recommendation}
            </div>

            <div className="igpe-card">
              <h4>Axes de priorités recommandés</h4>
              <div style={{ padding: '0.35rem 1rem 0.85rem' }}>
                <p className="muted" style={{ marginTop: 0 }}>
                  Dimensions dont le résultat est inférieur à 50 % du total cible.
                </p>
                {result.vulnerabilities.length === 0 ? (
                  <p style={{ marginBottom: 0 }}>Aucune dimension sous le seuil de vulnérabilité.</p>
                ) : (
                  <ul className="igpe-axes">
                    {result.vulnerabilities.map((v) => (
                      <li key={v.dimension}>
                        <span className="igpe-axes-pct">{Math.round(v.pct_cible)}%</span>
                        <div>
                          <strong>{v.dimension}</strong>
                          <div className="muted" style={{ fontSize: '0.9rem' }}>
                            {v.detail}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            <div className="igpe-card">
              <h4>Orientation pour l’interprétation</h4>
              <div className="table-wrap">
                <table className="igpe-interp">
                  <thead>
                    <tr>
                      <th>IGPE (%)</th>
                      <th>Niveau de maturité</th>
                      <th>Recommandation</th>
                    </tr>
                  </thead>
                  <tbody>
                    {IGPE_LEVEL_BANDS.map((b) => (
                      <tr
                        key={b.level}
                        className={result.igpe_level === b.level ? 'is-current' : undefined}
                      >
                        <td>
                          {b.min === 0 ? `< ${b.max + 1}` : `${b.min}–${b.max}`}
                        </td>
                        <td>{b.label}</td>
                        <td>{b.recommendation}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </article>
      ) : null}
    </div>
  );
}
